import React, { useState } from "react";
import { Box, Popover, Typography, Divider } from "@mui/material";
import { LuSlidersHorizontal, LuTable, LuCalculator, LuSparkles, LuLayers } from "react-icons/lu";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import { useTranslation } from "react-i18next";
import { useAuth } from "../authentication/AuthProvider";
import { ORG_PALETTE } from "../theme/style/organizationPalette";
import { getM3Colors, getM3Elevation } from "../theme/style/m3Palette";
import { M3Badge, M3Button } from "../components/m3";
import { StandardSwitch, StandardToggleRow } from "../components/switch/StandardToggle";

/**
 * Shared Hook for Circularity Admin Tabs
 * Persists admin preferences in localStorage under 'arivu_circularity_admin_tabs_prefs'.
 * Non-admins always get false for all tabs.
 */
export function useCircularityAdminTabs() {
  const { isAdmin } = useAuth();

  const [adminTabsPrefs, setAdminTabsPrefs] = useState(() => {
    try {
      const saved = localStorage.getItem("arivu_circularity_admin_tabs_prefs");
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error("Failed to load circularity admin tab preferences", e);
    }
    return {
      utility: false,
      mci_calculation: false,
      design_assessment: false,
      ai_recommendation: false,
    };
  });

  const updatePref = (key, value) => {
    setAdminTabsPrefs((prev) => {
      const next = { ...prev, [key]: value };
      try {
        localStorage.setItem("arivu_circularity_admin_tabs_prefs", JSON.stringify(next));
      } catch (e) {
        console.error("Failed to persist admin tab preferences", e);
      }
      return next;
    });
  };

  const setAllPrefs = (value) => {
    const next = {
      utility: value,
      mci_calculation: value,
      design_assessment: value,
      ai_recommendation: value,
    };
    try {
      localStorage.setItem("arivu_circularity_admin_tabs_prefs", JSON.stringify(next));
    } catch (e) {
      console.error("Failed to persist admin tab preferences", e);
    }
    setAdminTabsPrefs(next);
  };

  const showUtilityTab = !!isAdmin && !!adminTabsPrefs.utility;
  const showMciCalcTab = !!isAdmin && !!adminTabsPrefs.mci_calculation;
  const showDesignAssessmentTab = !!isAdmin && !!adminTabsPrefs.design_assessment;
  const showAiRecommendTab = !!isAdmin && !!adminTabsPrefs.ai_recommendation;

  const activeAdminTabsCount =
    (showUtilityTab ? 1 : 0) +
    (showMciCalcTab ? 1 : 0) +
    (showDesignAssessmentTab ? 1 : 0) +
    (showAiRecommendTab ? 1 : 0);

  return {
    isAdmin,
    adminTabsPrefs,
    updatePref,
    setAllPrefs,
    showUtilityTab,
    showMciCalcTab,
    showDesignAssessmentTab,
    showAiRecommendTab,
    activeAdminTabsCount,
  };
}

/**
 * CircularityAdminTabControls Component
 * Renders a clean admin button and popover with standard switches.
 * Fully left-aligned, standard toggle cards, no redundant subtext.
 */
const CircularityAdminTabControls = ({
  adminTabsPrefs,
  updatePref,
  setAllPrefs,
  activeAdminTabsCount = 0,
  isDark = false,
}) => {
  const { isAdmin } = useAuth();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);

  if (!isAdmin) return null;

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const m3 = getM3Colors(isDark);
  const elevation = getM3Elevation(isDark);

  const allActive =
    !!adminTabsPrefs.utility &&
    !!adminTabsPrefs.mci_calculation &&
    !!adminTabsPrefs.design_assessment &&
    !!adminTabsPrefs.ai_recommendation;

  const toggles = [
    { key: "utility", icon: LuTable, label: "Utility" },
    { key: "mci_calculation", icon: LuCalculator, label: "MCI Calculation" },
    { key: "design_assessment", icon: LuLayers, label: "Design Assessment" },
    { key: "ai_recommendation", icon: LuSparkles, label: "AI Recommendation" },
  ];

  return (
    <>
      <M3Button
        variant={activeAdminTabsCount > 0 ? "tonalPrimary" : "outlined"}
        icon={<LuSlidersHorizontal size={16} />}
        trailingIcon={activeAdminTabsCount > 0 ? <M3Badge size="sm" tone="primary">{activeAdminTabsCount}</M3Badge> : null}
        onClick={handleOpen}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {t("Admin Views")}
      </M3Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 290,
            borderRadius: ORG_PALETTE.radius.card,
            border: `1px solid ${m3.outlineVariant}`,
            bgcolor: m3.surfaceContainer,
            backgroundImage: "none",
            boxShadow: elevation[2],
            p: 2,
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.75, textAlign: "left" }}>
          <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 20, color: m3.primary }} />
          <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: ORG_PALETTE.font.size.bodyText, fontWeight: ORG_PALETTE.font.weight.semibold, color: m3.onSurface, textAlign: "left" }}>
            {t("Admin Workspace Views")}
          </Typography>
        </Box>

        <Box
          onClick={() => setAllPrefs(!allActive)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1.25,
            mb: 1.5,
            borderRadius: ORG_PALETTE.radius.input,
            bgcolor: allActive ? m3.secondaryContainer : "transparent",
            border: `1px solid ${allActive ? m3.primary : m3.outlineVariant}`,
            cursor: "pointer",
            transition: "border-color 0.2s ease, background-color 0.2s ease",
            "&:hover": { borderColor: m3.primary },
          }}
        >
          <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: ORG_PALETTE.font.size.caption, fontWeight: ORG_PALETTE.font.weight.semibold, color: allActive ? m3.onSecondaryContainer : m3.onSurfaceVariant, textAlign: "left" }}>
            {t("Enable All")}
          </Typography>
          <StandardSwitch checked={allActive} onChange={(val) => setAllPrefs(val)} activeColor={m3.primary} isDark={isDark} />
        </Box>

        <Divider sx={{ borderColor: m3.outlineVariant, my: 1.25 }} />

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {toggles.map((toggle) => (
            <StandardToggleRow
              key={toggle.key}
              icon={toggle.icon}
              label={toggle.label}
              checked={!!adminTabsPrefs[toggle.key]}
              onChange={(val) => updatePref(toggle.key, val)}
              activeColor={m3.primary}
              isDark={isDark}
            />
          ))}
        </Box>
      </Popover>
    </>
  );
};

export default CircularityAdminTabControls;

